import { useState } from 'react';
import { Button, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useLeaseTracts } from '../queries/lease-tracts';
import { TractFormDrawer } from '../routes/land-data/lease-tracts/TractFormDrawer';
import type { LeaseTract } from '../types/api';

interface Props {
  /** The saved lease's id, or null for a brand-new/unsaved lease. */
  leaseId: string | null;
}

function fmtAcres(v: number | string | null): string {
  if (v === null) return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  return n.toLocaleString();
}

/**
 * "Tracts" section for LeaseFormDrawer (spec docs/superpowers/specs/
 * 2026-08-16-land-data-lease-tracts-design.md §4.2) — rendered below the
 * obligation section. Lists the lease's tracts and opens TractFormDrawer
 * (stacked above the lease drawer) for both create ("+ Add tract", lease_id
 * preset) and edit (row click).
 *
 * When `leaseId` is null (a brand-new, unsaved lease) there is nothing to
 * add tracts to yet — the section renders a muted note instead of the live
 * controls, same "disabled-until-saved" pattern as CrossReferenceSection /
 * ObligationAssignmentSection.
 */
export function LeaseTractSection({ leaseId }: Props) {
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [editingTract, setEditingTract] = useState<LeaseTract | null>(null);

  const tractsQ = useLeaseTracts(
    leaseId ? { lease_id: leaseId } : {},
    { enabled: leaseId != null },
  );

  function openNew() {
    setEditingTract(null);
    setDrawerOpened(true);
  }

  function openEdit(tract: LeaseTract) {
    setEditingTract(tract);
    setDrawerOpened(true);
  }

  if (leaseId == null) {
    return (
      <Stack gap="xs">
        <Title order={5}>Tracts</Title>
        <Text size="sm" c="dimmed">
          Save the lease first to add tracts.
        </Text>
      </Stack>
    );
  }

  const tracts = tractsQ.data ?? [];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={5}>Tracts</Title>
        <Button size="xs" variant="light" onClick={openNew}>
          + Add tract
        </Button>
      </Group>

      {tracts.length === 0 ? (
        <Text size="xs" c="dimmed">
          No tracts yet
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Agreement #</Table.Th>
              <Table.Th>Lessor</Table.Th>
              <Table.Th>Lessee</Table.Th>
              <Table.Th>Gross acres</Table.Th>
              <Table.Th>Net acres</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tracts.map((t) => (
              <Table.Tr
                key={t.id}
                onClick={() => openEdit(t)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>{t.agreement_number}</Table.Td>
                <Table.Td>{t.lessor ?? '—'}</Table.Td>
                <Table.Td>{t.lessee ?? '—'}</Table.Td>
                <Table.Td>{fmtAcres(t.gross_acres)}</Table.Td>
                <Table.Td>{fmtAcres(t.net_acres)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <TractFormDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        leaseId={leaseId}
        tract={editingTract}
      />
    </Stack>
  );
}
